import * as THREE from 'three';
import { EnemyModel, HITBOX_DEFS } from '../ai/EnemyModel.js';
import { TEAM } from '../game/MatchAuthority.js';

/**
 * Another human, drawn and shot at exactly like a bot.
 *
 * ## The one thing worth noticing here
 *
 * This registers with `physics` and with `match` using the same two contracts an
 * `Enemy` uses, and that is why the rest of the game needs no changes at all to
 * support other players. `Ballistics` raycasts against physics actors — it does
 * not care that this one's position arrives over a network. `MatchAuthority`
 * arbitrates anything with `id`, `health`, `applyDamage` and `applyDeath`.
 * The work done extracting the authority is what makes this file short.
 *
 * ## Interpolation, and why it is not prediction
 *
 * Snapshots arrive 50 ms apart and frames are drawn every 16 ms, so two frames in
 * three have no data of their own. This holds a small buffer and draws the world
 * as it was `interpDelay` ago, between two snapshots we already have.
 *
 * The alternative — extrapolating forward from the last known velocity — makes the
 * remote player *arrive* on time and be wrong about it: they keep running after
 * they stopped, and then snap back. Being 100 ms behind and correct is the better
 * trade for a body you are aiming at, because your shot is judged by the host
 * against the same history. Prediction is for the player's own character, where
 * being behind is unbearable and being wrong is invisible.
 */

export const TUNING = {
  /**
   * How far behind live we draw remote players. Two snapshot intervals at 20 Hz,
   * so a single dropped packet still has a later one to interpolate toward.
   */
  interpDelayMs: 110,
  /** Buffer beyond this is a stall; snap rather than crawl through stale frames. */
  maxBufferMs: 1000,
  /** Teleport instead of interpolating past this distance — a respawn, not a run. */
  snapDistance: 6,
  health: 100,
  standHeight: 1.8,
  eyeHeight: 1.62,
  crouchEyeHeight: 1.05,
};

let _seq = 0;

export class RemotePlayer {
  constructor(ctx, { slot, name = 'PLAYER', team = TEAM.BRAVO }) {
    this.ctx = ctx;
    this.slot = slot;
    this.name = name;
    this.team = team;
    this.id = `net_${slot}_${_seq++}`;

    this.position = new THREE.Vector3();
    this.velocity = new THREE.Vector3();
    this.yaw = 0;
    this.pitch = 0;
    this.crouching = false;
    this.ads = false;
    this.dead = false;
    this.health = TUNING.health;

    /** { t, x, y, z, yaw, pitch, flags } newest last. */
    this._buf = [];
    this._lastFiring = false;

    this.model = new EnemyModel(ctx, slot, team);
    ctx.scene.add(this.model.root);

    /* ---- the same actor contract an Enemy uses ---- */
    this.boundingRadius = 1.95;
    this.matrixWorld = new THREE.Matrix4();
    this.hitboxes = HITBOX_DEFS.map((d) => ({
      offset: new THREE.Vector3(d.offset[0], d.offset[1], d.offset[2]),
      halfExtents: new THREE.Vector3(d.half[0], d.half[1], d.half[2]),
      tag: d.tag,
      damageScale: d.damageScale,
      bone: this.model.bone[d.bone],
      boneName: d.bone,
      worldMatrix: new THREE.Matrix4(),
    }));
    ctx.physics.registerActor(this);
    ctx.match?.register(this, {
      team,
      kind: 'player',
      name,
      /** Not a bot, so the pacing curve must not count them as one. */
      bot: false,
    });
  }

  get eye() {
    return this.crouching ? TUNING.crouchEyeHeight : TUNING.eyeHeight;
  }

  get speed() {
    return Math.hypot(this.velocity.x, this.velocity.z);
  }

  /** A transform straight off the wire. `t` is our own clock, not theirs. */
  push(sample, now) {
    const buf = this._buf;
    buf.push({ ...sample, t: now });
    while (buf.length > 2 && now - buf[0].t > TUNING.maxBufferMs) buf.shift();
  }

  update(dt, now) {
    const buf = this._buf;
    if (!buf.length) return;

    const target = now - TUNING.interpDelayMs;

    // Drop everything older than the pair we need, but never the last two.
    while (buf.length > 2 && buf[1].t <= target) buf.shift();

    let x, y, z, yaw, pitch, flags;
    if (buf.length === 1 || buf[0].t >= target) {
      const s = buf[0];
      ({ x, y, z, yaw, pitch, flags } = s);
    } else {
      const a = buf[0];
      const b = buf[1];
      const span = Math.max(1, b.t - a.t);
      const k = Math.max(0, Math.min(1, (target - a.t) / span));
      x = a.x + (b.x - a.x) * k;
      y = a.y + (b.y - a.y) * k;
      z = a.z + (b.z - a.z) * k;
      yaw = a.yaw + shortestAngle(a.yaw, b.yaw) * k;
      pitch = a.pitch + (b.pitch - a.pitch) * k;
      flags = k < 0.5 ? a.flags : b.flags;
    }

    const prevX = this.position.x;
    const prevZ = this.position.z;
    // A respawn is not a sprint: interpolating across one draws a body skating
    // through walls for a second and a half.
    const jumped = Math.hypot(x - prevX, z - prevZ) > TUNING.snapDistance;
    if (jumped) this._buf.length = Math.min(this._buf.length, 1);

    this.position.set(x, y, z);
    if (dt > 0 && !jumped) {
      this.velocity.set((x - prevX) / dt, 0, (z - prevZ) / dt);
    } else {
      this.velocity.set(0, 0, 0);
    }
    this.yaw = yaw;
    this.pitch = pitch;

    this.crouching = !!(flags & 1);
    this.ads = !!(flags & 2);
    const wasDead = this.dead;
    this.dead = !!(flags & 8);
    if (wasDead !== this.dead) this._onDeadChanged();

    this._drive(dt);
  }

  /**
   * The animation rig wants the same handful of numbers the AI feeds it, so a
   * remote body walks, aims and leans exactly like a bot does. Nothing in
   * `EnemyModel` knows or needs to know where the numbers came from.
   */
  _drive(dt) {
    const m = this.model;
    m.yaw = this.yaw;
    m.speed = this.speed;
    m.crouch = this.crouching ? 1 : 0;
    m.aimYaw = this.yaw;
    m.aimPitch = this.pitch;
    m.holdWeight = 1;
    m.aimWeight = this.ads ? 1 : 0.55;
    m.root.position.copy(this.position);
    m.update(dt, null);
    for (const hb of this.hitboxes) hb.worldMatrix.copy(hb.bone.matrixWorld);
    this.matrixWorld.copy(m.root.matrixWorld);
  }

  _onDeadChanged() {
    if (this.dead) {
      this.ctx.physics.removeActor(this.id);
      this.model.startRagdoll(
        new THREE.Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw)),
        1,
        'chest',
        this.velocity,
        this.position.y
      );
    } else {
      this.ctx.physics.registerActor(this);
      this.ctx.match?.revive(this);
      this.model.dead = false;
      this.health = TUNING.health;
    }
  }

  /* --------------------------- authority contract -------------------------- */

  /**
   * Only ever called on the host. A client's copy of a remote player has its
   * health written from the snapshot instead, because a client that decided
   * damage locally would be a client that disagrees.
   */
  applyDamage(res) {
    this.health = res.after;
    this.model.flinch(
      res.dir ?? new THREE.Vector3(0, 0, 1),
      res.hitbox?.boneName ?? 'chest',
      Math.max(0.25, Math.min(1.1, res.amount / 40))
    );
  }

  applyDeath(res) {
    if (this.dead) return;
    this.dead = true;
    this.health = 0;
    this.ctx.physics.removeActor(this.id);
    this.model.startRagdoll(
      res?.dir ?? new THREE.Vector3(0, 0, 1),
      res?.headshot ? 1.8 : 1,
      res?.hitbox?.tag ?? 'chest',
      this.velocity,
      this.position.y
    );
    this.ctx.events.emit('audio:play', {
      id: 'enemy_death',
      position: this.position.clone(),
      volume: 0.9,
    });
  }

  respawn(pos) {
    this.dead = false;
    this.health = TUNING.health;
    if (pos) this.position.copy(pos);
    this.ctx.match?.revive(this);
    this.ctx.physics.registerActor(this);
  }

  dispose() {
    this.ctx.physics.removeActor(this.id);
    this.ctx.match?.unregister(this.id);
    this.ctx.scene.remove(this.model.root);
    this.model.dispose();
  }
}

/** Shortest signed way round the circle from a to b. */
function shortestAngle(a, b) {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  else if (d < -Math.PI) d += Math.PI * 2;
  return d;
}
